import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import TableProvider, { useTableState } from '../Table/TableProvider'
import BootstrapTable from 'react-bootstrap-table-next'
import UniversalTableWrapperTest from '../Table/UniversalTableWrapperTest'
import paginationFactory from 'react-bootstrap-table2-paginator'
import { formatedDate } from '../../utils/formatedDate'
import { formatedPrice } from '../../utils/formatedPrice'
import styled from 'styled-components'
import { Spinner } from 'react-bootstrap'
import { usePAIOrdersState } from '../../stores/PAIOrdersTableProvider'
import useReport from '../../utils/hooks/useReport'
import PdfIcon from '../../assets/icons/pdf.svg'
import { APIEndpoints } from '../../utils/api/apiConfig'
import { Observer } from 'mobx-react'

const PdfIconBtn = styled.span`
  background: url(${PdfIcon}) center center no-repeat;
  background-size: contain;
  display: inline-block;
  width: 20px;
  height: 20px;
  line-height: 20px;
  vertical-align: middle;

  :hover {
    opacity: 0.8;
  }
`

const StyledReportsTableWrapper = styled.div`
  .react-bootstrap-table {
    background: white;
  }
`

const StyledHeaderCell = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const PAIOrdersTableInner = () => {
  const { t } = useTranslation()
  const {
    tableRef,
    paginationOptions,
    setPageLimit,
    setPageOffset,
    currentPage,
    totalPages,
  } = useTableState()
  const {
    onRowClick,
    tableIsLoading,
    incomingOrders,
    allSelectedLoading,
    selectAll,
    allSelected,
  } = usePAIOrdersState()
  const { createReport } = useReport()
  const loadPdf = useCallback((orderId) => {
    createReport("reportName", APIEndpoints.paiOrders(orderId).receipt, {})
  },
    [createReport]
  )
  const columns = useMemo(
    () => [
      {
        dataField: 'checkBox',
        text: '',
        headerClasses: 'checkBox-header-cell',
        classes: 'checkBox-cell',
      },
      {
        dataField: '__numbers',
        text: t('paiOrders:orderNumber'),
        formatter: (cell, row) => (
          <>
            <>
              {row.internalNumber && <div>
                <b>{t('paiOrders:internal')}:</b> {row.internalNumber}
              </div>}
              {row.externalNumber && <div>
                <b>{t('paiOrders:external')}:</b> {row.externalNumber}
              </div>}
            </>
          </>
        ),
      },
      {
        dataField: 'createdAt',
        text: t('complaints:createdAt'),
        formatter: (cell) => <div>{formatedDate(cell)}</div>,
      },
      {
        dataField: 'creator.displayName',
        text: t('reports:createdBy'),
      },
      {
        dataField: 'supplier.displayName',
        text: t('supplier'),
      },
      {
        dataField: '__amounts',
        text: t('paiOrders:amount'),
        formatter: (cell, row) => <div>{formatedPrice(row.price, row.currency)}</div>,
      },
      {
        dataField: '__actions',
        text: '',
        sort: false,
        headerClasses: 'actions-header-cell',
        formatter: (cell, row, rowIndex, extraProps) => (
          <span title={t('paiOrders:detailsAsPDF')} role="button" onClick={(e) => loadPdf(row.id)}>
            <PdfIconBtn />
          </span>
        ),
      },
    ],
    [t, loadPdf]
  )
  const handleTableChange = (type, newState) => {
    setPageLimit(newState.sizePerPage)
    setPageOffset(
      newState.page === 1
        ? 0
        : newState.sizePerPage * newState.page - newState.sizePerPage
    )
  }
  const preparedColumns = useMemo(() => {
    const tmpColumns = [...columns]
    tmpColumns.forEach((tmpColumn) => {
      tmpColumn.headerFormatter = (column, colIndex, components) => {
        switch (tmpColumn.dataField) {
          case 'checkBox':
            return (
              <span>
                {allSelectedLoading ? (
                  <Spinner animation='border' variant='primary' size='sm' />
                ) : (
                  <input
                    type='checkbox'
                    id='checkbox_header'
                    name='scales'
                    checked={allSelected}
                    onChange={selectAll}
                  ></input>
                )}
              </span>
            )
          default:
            return (
              <StyledHeaderCell title={column.text}>
                {column.text}
              </StyledHeaderCell>
            )
        }
      }
    })
    return tmpColumns
  }, [columns, allSelectedLoading, allSelected, selectAll])

  return (<Observer>{() => (
    <StyledReportsTableWrapper>
      <UniversalTableWrapperTest
        tableIsLoading={tableIsLoading}
        currentPage={currentPage}
        totalPages={totalPages}
      >
        <BootstrapTable
          ref={tableRef}
          remote
          noDataIndication={() => (
            <div className='no-data-placeholder'>{t('feeds:noEntries')}</div>
          )}
          loading={tableIsLoading}
          keyField='id'
          data={incomingOrders}
          columns={preparedColumns}
          bootstrap4
          hover
          bordered={false}
          pagination={paginationFactory(paginationOptions)}
          onTableChange={handleTableChange}
          rowEvents={{ onClick: onRowClick }}
        />
      </UniversalTableWrapperTest>
    </StyledReportsTableWrapper>
  )}</Observer>)
}

const PAIOrdersTable = () => {
  //communication between the two stores
  const {
    pageOffset,
    setPageOffset,
    pageLimit,
    setPageLimit,
    totalCount,
    setTotalCount,
  } = usePAIOrdersState()

  const providerSettings = {
    pageOffset,
    setPageOffset,
    pageLimit,
    setPageLimit,
    totalCount,
    setTotalCount,
  }
  return (
    <TableProvider {...providerSettings}>
      <PAIOrdersTableInner />
    </TableProvider>
  )
}

export default PAIOrdersTable
